import * as React from "react"

const MOBILE_BREAKPOINT = 768

const MOBILE_QUERY = `(max-width: ${MOBILE_BREAKPOINT - 1}px)`

/**
 * True when the viewport is phone-sized.
 *
 * Width is the whole signal — no user-agent sniffing. There's no SSR here (a
 * plain Vite SPA), so the real viewport is always measurable, and UA strings
 * lie anyway: iPadOS Safari ships a desktop UA by default. A desktop user who
 * narrows their window gets the mobile app, which is intended.
 *
 * The initial value is read synchronously rather than in an effect. Callers
 * that only pick a widget variant (the sidebar's sheet, a chart layout) can
 * tolerate a one-frame flip, but `MobileShell` chooses the whole app shell —
 * resolving late there would paint the desktop tree and then remount every
 * route, query and chat stream.
 */
export function useIsMobile() {
  const [isMobile, setIsMobile] = React.useState(
    () => typeof window !== "undefined" && window.matchMedia(MOBILE_QUERY).matches
  )

  React.useEffect(() => {
    const mql = window.matchMedia(MOBILE_QUERY)
    const onChange = () => setIsMobile(mql.matches)
    mql.addEventListener("change", onChange)
    onChange()
    return () => mql.removeEventListener("change", onChange)
  }, [])

  return isMobile
}

/**
 * Embedded analytics runs inside an iframe that is often narrower than the
 * mobile breakpoint, and it must keep rendering the dashboard it was pointed
 * at rather than our phone app. Captured once at module load — whether we are
 * framed can't change for the life of the document.
 */
const isFramed = typeof window !== "undefined" && window.self !== window.top

/**
 * True when this document should render the pared-down mobile app rather than
 * the full desktop one. Use this — not `useIsMobile` — for anything that swaps
 * a whole screen; `useIsMobile` still means "the viewport is narrow", which an
 * embed can be without being a phone.
 */
export function useIsMobileApp() {
  return useIsMobile() && !isFramed
}
